// StackedCountChart.tsx
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from 'recharts';
import { axisMaxByMSD, toNum } from '../graph/chartUtils';
import { formatCurrency } from '../../../../utils/Function';

type Props = {
  data: any[];
  height?: number;
  tickWidth?: number;
  leftAxisWidth?: number;
  title?: string;
};

const H_DEFAULT = 320;
const TICK_W_DEFAULT = 56;
const LEFT_W_DEFAULT = 50;

function CustomBarTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  const viewMeta = [
    {
      key: 'mortgage_uk',
      label: '근저당액',
      img: '/assets/images/icon/gray_box.svg',
    },
    {
      key: 'claim_uk',
      label: '청구금액',
      img: '/assets/images/icon/dark_gray_box.svg',
    },
  ];

  return (
    <TooltipContainer>
      <TooltipHeader>{label}</TooltipHeader>
      <SeriesList>
        {viewMeta.map((meta) => {
          const v = payload.find((p: any) => p.dataKey === meta.key);
          if (!v || v.value === undefined || v.value === null || Number.isNaN(v.value)) return null;
          return (
            <SeriesItem key={meta.key}>
              <SeriesIcon>
                <img src={meta.img} alt="" width={16} height={16} />
              </SeriesIcon>
              <SeriesLabel>{meta.label}</SeriesLabel>
              <SeriesValue style={{ marginLeft: 'auto' }}>
                {formatCurrency(Number(v.value) * 1e8)}
              </SeriesValue>
            </SeriesItem>
          );
        })}
      </SeriesList>
    </TooltipContainer>
  );
}

function StackedCountChartBase({
  data,
  height = H_DEFAULT,
  tickWidth = TICK_W_DEFAULT,
  leftAxisWidth = LEFT_W_DEFAULT,
}: Props) {
  // 억원 단위 변환 및 렌더 데이터
  const amountData = useMemo(
    () =>
      (data ?? []).map((d) => ({
        ...d,
        mortgage_uk: toNum(d.mortgageAmount) / 1e8,
        claim_uk: toNum(d.claimPrice) / 1e8,
      })),
    [data],
  );

  const amountAxisMax = useMemo(() => {
    const maxVal = Math.max(
      0,
      ...amountData.map((d) =>
        Math.max(toNum(d.mortgage_uk), toNum(d.claim_uk)),
      ),
    );
    return axisMaxByMSD(maxVal);
  }, [amountData]);

  const innerWidth = Math.max(tickWidth * data.length + 100, 480);

  return (
    <Wrap data-capture-section>
      <Grid style={{ gridTemplateColumns: `${leftAxisWidth}px minmax(0,1fr)` }}>
        {/* 좌측 축 */}
        <AxisPane>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={amountData}
              margin={{ top: 22, right: 10, left: -10, bottom: 39 }}
            >
              <XAxis dataKey="label" hide />
              <YAxis
                domain={[0, amountAxisMax]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                axisLine
                tickLine={{ stroke: '#B9BFC9', strokeWidth: 1 }}
                tickFormatter={(v) => Number(v).toLocaleString('ko-KR')}
              >
                <Label
                  value="금액(억)"
                  position="insideTopLeft"
                  angle={0}
                  offset={30}
                  dy={-50}
                  dx={-20}
                  style={{ fill: '#787D87', fontSize: 12, fontWeight: 500 }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>

        {/* 메인 플롯 + X축 (가로 스크롤) */}
        <ScrollPane data-export-expand>
          <Inner style={{ width: innerWidth, height }} data-export-inner>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={amountData}
                margin={{ top: 16, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  axisLine
                  tickLine={{ stroke: '#B9BFC9', strokeWidth: 1 }}
                />
                <YAxis hide domain={[0, amountAxisMax]} />
                <Tooltip
                  content={<CustomBarTooltip />}
                  cursor={{ stroke: '#B9BFC9', strokeDasharray: '3 3' }}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar
                  dataKey="mortgage_uk"
                  name="근저당액"
                  fill="#E8E9ED"
                  barSize={22}
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="claim_uk"
                  name="청구금액"
                  fill="#424854"
                  barSize={22}
                  radius={[8, 8, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
      </Grid>
    </Wrap>
  );
}

export default memo(StackedCountChartBase);

const Wrap = styled.div``;

const Grid = styled.div`
  display: grid;
  align-items: start;
  background: #fff;
`;

const AxisPane = styled.div``;

const ScrollPane = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Inner = styled.div`
  padding-bottom: 8px;
`;

const TooltipContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 6px 30px 0px rgba(56, 65, 87, 0.18);
  padding: 16px 18px 12px 18px;
  min-width: 186px;
  max-width: 400px;
`;

const TooltipHeader = styled.div`
  font-weight: 500;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: -2%;
  color: #424854;
  margin-bottom: 16px;
`;

const SeriesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px 0;
`;

const SeriesItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 8px 0;
`;

const SeriesIcon = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SeriesLabel = styled.span`
  font-size: 13px;
  color: #676e7e;
  min-width: 74px;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: 500;
`;

const SeriesValue = styled.div`
  font-size: 14px;
  color: #111322;
  font-weight: 500;
  white-space: nowrap;
`;

